import "./styles.css";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { BaseButton, BaseFormCard, BaseTextField } from "../../base";
import CloseIcon from "../../../assets/icons/CloseIcon";
import { passwordUpdateFailedGeneric } from "./validation_messages";
import { ECAxiosAPI, EcUserDataContext } from "@eclear-ag/ec-common-ui";
import { URLS } from "../../../configuration/urls";
import UserContext from "../../../services/user/user";
import messages_de from "../../../translations/de.json";
import messages_en from "../../../translations/en.json";
import { FormattedMessage } from "react-intl";

// Individual password rules — shown as a checklist under New Password,
// identical to ec-main-ui's register screen (same regexes, i18n keys, order).
const getPasswordRules = (password) => [
  { id: "password.error", valid: password.length >= 8 },
  {
    id: "password.capitalSmall",
    valid: /[A-Z]/.test(password) && /[a-z]/.test(password)
  },
  { id: "password.digit", valid: /\d/.test(password) },
  {
    id: "password.specialCharacter",
    valid: /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password)
  }
];

const initialState = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: ""
};

export default function PasswordForm({
  setScreen,
  setIsModalVisible,
  setFinalData,
  onClose
}) {
  const { userData } = useContext(EcUserDataContext);
  const { lang } = useContext(UserContext);

  // BaseTextField is react-hook-form based: it owns the input value via its
  // Controller, so rhf is the value source. We keep `formData` as the source
  // of truth for validation and the API call, mirroring each rhf value change
  // into it (below) and deriving the per-field rules from it — matching
  // ec-main-ui's register screen (submit-gated, value-derived validation).
  const { control, watch } = useForm({
    mode: "onChange",
    defaultValues: initialState
  });

  const watchedCurrent = watch("currentPassword");
  const watchedNew = watch("newPassword");
  const watchedConfirm = watch("confirmPassword");

  // The old EcTextField only validated on a real keystroke (its onChange), not
  // on mount. The watch effects below fire once on mount with the empty initial
  // values, so skip that first run per field to keep behavior identical.
  const didMountCurrent = useRef(false);
  const didMountNew = useRef(false);
  const didMountConfirm = useRef(false);

  const [formData, setFormData] = useState(initialState);
  const [isMakingApiCall, setIsMakingApiCall] = useState(false);
  // Validation only runs on submit: per-field errors show only after a submit
  // attempt, matching ec-main-ui's register screen.
  const [submitted, setSubmitted] = useState(false);
  const [currentPasswordApiError, setCurrentPasswordApiError] = useState(null);

  const messages = {
    de: messages_de,
    en: messages_en
  };

  const passwordRules = getPasswordRules(formData.newPassword);
  const isPasswordValid = passwordRules.every((rule) => rule.valid);
  const isConfirmValid =
    formData.confirmPassword !== "" &&
    formData.newPassword === formData.confirmPassword;
  const isCurrentPasswordEmpty = formData.currentPassword.length === 0;

  const showPasswordRules = submitted && !isPasswordValid;
  const showConfirmError = submitted && !isConfirmValid;
  const showCurrentPasswordError = submitted && isCurrentPasswordEmpty;

  const submitCurrentPass = async () => {
    setIsMakingApiCall(true);
    await ECAxiosAPI.post(`${URLS.REQUEST_PASSWORD_UPDATING()}`, {
      email: userData.userInfo.email,
      password: formData.currentPassword
    })
      .then((res) => {
        setFinalData({
          email: userData.userInfo.email,
          ...formData
        });
        setCurrentPasswordApiError(null);
        setScreen("code");
        setIsMakingApiCall(false);
      })
      .catch((err) => {
        setCurrentPasswordApiError(passwordUpdateFailedGeneric);
        setIsMakingApiCall(false);
      });
  };

  useEffect(() => {
    if (!didMountCurrent.current) {
      didMountCurrent.current = true;
      return;
    }
    setFormData((prev) => ({ ...prev, currentPassword: watchedCurrent }));
    setCurrentPasswordApiError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCurrent]);

  useEffect(() => {
    if (!didMountNew.current) {
      didMountNew.current = true;
      return;
    }
    setFormData((prev) => ({ ...prev, newPassword: watchedNew }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedNew]);

  useEffect(() => {
    if (!didMountConfirm.current) {
      didMountConfirm.current = true;
      return;
    }
    setFormData((prev) => ({ ...prev, confirmPassword: watchedConfirm }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedConfirm]);

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setCurrentPasswordApiError(null);

    if (!isCurrentPasswordEmpty && isPasswordValid && isConfirmValid) {
      submitCurrentPass();
    }
  };

  return (
    <BaseFormCard
      title="Change Password"
      headerExtra={
        <IconButton
          onClick={onClose || (() => setIsModalVisible(false))}
          sx={{ ml: "auto", p: "4px" }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      }
      onSubmit={onSubmit}
      footer={
        <>
          <BaseButton
            data-testid="close-button"
            variant="text"
            onClick={() => {
              (onClose || (() => setIsModalVisible(false)))();
            }}
          >
            <FormattedMessage id="cancel" />
          </BaseButton>
          <BaseButton
            data-testid="update-password-button"
            disabled={isMakingApiCall}
            loading={isMakingApiCall}
            loadingTestId="update-password-loading"
            variant="filled"
            type="submit"
          >
            <FormattedMessage id="Update Password" />
          </BaseButton>
        </>
      }
    >
      <Box
        sx={{
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "16px"
        }}
      >
        <Box>
          <BaseTextField
            errors={
              showCurrentPasswordError ? { currentPassword: { type: "required" } } : {}
            }
            errorTestId="current-password-error"
            control={control}
            disabled={isMakingApiCall}
            label={"currentPassword"}
            required
            placeholder={messages[lang]["Please enter your current password"]}
            autoFocus
            type="password"
            inputProps={{
              "data-testid": "current-password-input"
            }}
          />
          {currentPasswordApiError && (
            <Typography
              component="span"
              data-testid="current-password-api-error"
              className="text-error"
              sx={{ fontSize: 12, fontWeight: 400, lineHeight: 1.67 }}
            >
              <FormattedMessage
                id={currentPasswordApiError[0].messages[0].text}
              />
            </Typography>
          )}
        </Box>
        <Box>
          <BaseTextField
            errors={{}}
            forceError={showPasswordRules && !isPasswordValid}
            control={control}
            label={"newPassword"}
            required
            placeholder={messages[lang]["Please enter your new password"]}
            type="password"
            inputProps={{
              "data-testid": "new-password-input"
            }}
          />
          <Collapse in={showPasswordRules}>
            <Box
              data-testid="password-rules"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                mt: "2px"
              }}
            >
              {passwordRules.map((rule) => (
                <Typography
                  key={rule.id}
                  component="span"
                  data-testid={`password-rule-${rule.id}`}
                  className={rule.valid ? undefined : "text-error"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: 1.67
                  }}
                >
                  {rule.valid ? (
                    <svg
                      aria-hidden="true"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0 }}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg
                      aria-hidden="true"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0 }}
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  )}
                  <FormattedMessage id={rule.id} />
                </Typography>
              ))}
            </Box>
          </Collapse>
        </Box>
        <Box>
          <BaseTextField
            errors={{}}
            forceError={showConfirmError}
            control={control}
            label={"confirmPassword"}
            required
            placeholder={messages[lang]["Please confirm your new password"]}
            type="password"
            inputProps={{
              "data-testid": "repeat-new-password-input"
            }}
          />
          <Collapse in={showConfirmError}>
            <Typography
              component="span"
              data-testid="repeat-password-error"
              className="text-error"
              sx={{ fontSize: 12, fontWeight: 400, lineHeight: 1.67 }}
            >
              <FormattedMessage id="password.noMatch" />
            </Typography>
          </Collapse>
        </Box>
      </Box>
    </BaseFormCard>
  );
}
